import { useState, useRef, useCallback, useEffect } from 'react';
import { unlockAudio } from '../utils/audioUnlock';
import { speakNative, stopNativeTTS } from '../utils/nativeTts';
import { playAudioBuffer } from '../utils/webAudioPlayer';
import { stopAllAudio } from '../utils/audioManager';

const API_BASE = '';

function getAgentXaiVoice(agentNameOrId) {
  try {
    const raw = localStorage.getItem('cogency_agent_xai_voices');
    if (!raw) return null;
    const map = JSON.parse(raw);
    return map[agentNameOrId] || map[String(agentNameOrId).toLowerCase()] || null;
  } catch {
    return null;
  }
}

const testPhrases = {
  en: 'Hello, this is the selected voice.',
  fr: 'Bonjour, ceci est la voix sélectionnée.',
  es: 'Hola, esta es la voz seleccionada.',
  de: 'Hallo, dies ist die ausgewählte Stimme.',
  ar: 'مرحباً، هذا هو الصوت المختار.'
};

function getToken() {
  return localStorage.getItem('cogency_token') || '';
}

function getSummaryLevel() {
  try {
    const saved = localStorage.getItem('cogency_voice_summary_level');
    return saved ? parseInt(saved, 10) : 0;
  } catch {
    return 0;
  }
}

export function useVoice() {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [voiceSettings, setVoiceSettings] = useState(null);
  const [voiceStatus, setVoiceStatus] = useState({ espeak: false, whisper: false });
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [voices, setVoices] = useState([]);
  const [voicesByLang, setVoicesByLang] = useState({});
  const [voiceId, setVoiceId] = useState(() => localStorage.getItem('cogency_voice_id') || '');
  const [isTestingVoice, setIsTestingVoice] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioRef = useRef(new Audio());

  useEffect(() => {
    fetchVoiceSettings();
    fetch(`${API_BASE}/api/tts-voices`)
      .then(r => r.json())
      .then(data => {
        if (data.voices) setVoices(data.voices);
        if (data.byLang) setVoicesByLang(data.byLang);
        if (!voiceId) {
          const settingsLang = voiceSettings?.language || 'fr';
          const langVoices = data.byLang?.[settingsLang];
          const selected = langVoices?.[0]?.id || data.default;
          if (selected) setVoiceId(selected);
        }
      })
      .catch(() => {});
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      stopNativeTTS();
    };
  }, []);

  useEffect(() => {
    if (voiceId) localStorage.setItem('cogency_voice_id', voiceId);
  }, [voiceId]);

  const fetchVoiceSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/voice/settings`);
      const data = await res.json();
      if (data.settings) setVoiceSettings(data.settings);
      if (data.availability) setVoiceStatus(data.availability);
    } catch (err) {
      console.error('Failed to fetch voice settings:', err);
    }
  };

  const updateVoiceSettings = async (updates) => {
    try {
      const res = await fetch(`${API_BASE}/api/voice/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.settings) setVoiceSettings(data.settings);
      return data;
    } catch (err) {
      console.error('Failed to update voice settings:', err);
      return { success: false, error: err.message };
    }
  };

  const startListening = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Microphone access not supported in this browser.');
      return;
    }
    // Stop any ongoing speech before listening to avoid feedback loops.
    stopAllAudio();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
        stream.getTracks().forEach(t => t.stop());
      };
      
      mediaRecorder.start();
      setIsListening(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(d => d + 1);
      }, 1000);
    } catch (err) {
      console.error('Microphone error:', err);
      alert('Could not access microphone. Please check permissions.');
    }
  }, []);

  const stopListening = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
    clearInterval(recordingTimerRef.current);
  }, []);

  const processAudio = async (audioBlob) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        const res = await fetch(`${API_BASE}/api/voice/stt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({ audio: base64 })
        });
        const data = await res.json();
        if (data.success && data.text) {
          setTranscript(data.text);
        } else {
          tryBrowserSTT();
        }
      };
    } catch (err) {
      console.error('STT error:', err);
      tryBrowserSTT();
    }
  };

  const tryBrowserSTT = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setTranscript('');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = voiceSettings?.language === 'fr' ? 'fr-FR' : 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (e) => {
      setTranscript(e.results[0][0].transcript);
    };
    recognition.onerror = () => setTranscript('');
    recognition.start();
  };

  const speak = useCallback(async (text, options = {}) => {
    if (!text) return;
    unlockAudio();

    // Ensure no other audio is currently playing before starting new speech.
    stopAllAudio();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }

    const settings = voiceSettings || await fetchVoiceSettings().then(() => voiceSettings);
    if (settings && !settings.enabled && !options.force) return;
    if (settings && settings.outputMode === 'text') return;
    
    setIsSpeaking(true);
    
    try {
      const cleanText = cleanTextForSpeech(text);
      const summaryLevel = getSummaryLevel();
      const res = await fetch(`${API_BASE}/api/voice/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          text: cleanText,
          options: {
            ...options,
            voiceId: options.voiceId || voiceId,
            voice: options.voice || getAgentXaiVoice(options.agentId),
            summarize: summaryLevel > 0,
            summaryLevel
          }
        })
      });

      if (res.ok) {
        const blob = await res.blob();
        try {
          await playAudioBuffer(blob, {
            onStart: () => setIsSpeaking(true),
            onEnd: () => setIsSpeaking(false),
          });
        } catch (playErr) {
          console.error('Audio play failed:', playErr);
          setIsSpeaking(false);
          await fallbackTTS(text, options.agentId);
        }
        return;
      }
    } catch (err) {
      console.log('Server TTS failed, using fallback:', err.message);
    }

    fallbackTTS(text, options.agentId);
  }, [voiceSettings, voiceId]);

  const cleanTextForSpeech = (text) => {
    // Strip markdown and special characters for TTS
    return text
      .replace(/!\[[^\]]*\]\([^)]+\)/g, "")           // images
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")       // links -> text
      .replace(/#{1,6}\s*/g, "")                         // headers
      .replace(/\*\*?|__?/g, "")                         // bold/italic
      .replace(/`{1,3}[^`]*`{1,3}/g, "")               // inline code
      .replace(/```[\s\S]*?```/g, "")                    // code blocks
      .replace(/\|/g, " ")                               // table pipes
      .replace(/[-*+]\s+/g, "")                          // list bullets
      .replace(/\d+\.\s+/g, "")                         // numbered lists
      .replace(/>\s*/g, "")                               // blockquotes
      .replace(/\]/g, "")                                // closing bracket
      .replace(/\[/g, "")                                // opening bracket
      .replace(/\(/g, "")                                // opening paren
      .replace(/\)/g, "")                                // closing paren
      .replace(/\|/g, "")                                // pipe
      .replace(/\\/g, "")                              // backslash
      .replace(/`/g, "")                                 // backtick
      .replace(/[*#=_^~]/g, "")                          // other markdown chars
      .replace(/\s+/g, " ")                               // collapse whitespace
      .trim();
  };

  const speakSummary = useCallback(async (text, options = {}) => {
    if (!text) return;
    unlockAudio();

    // Ensure no other audio is currently playing before starting new speech.
    stopAllAudio();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }

    const settings = voiceSettings || await fetchVoiceSettings().then(() => voiceSettings);
    if (settings && !settings.enabled && !options.force) return;
    if (settings && settings.outputMode === 'text') return;

    setIsSpeaking(true);

    try {
      const cleanText = cleanTextForSpeech(text);
      const summaryLevel = getSummaryLevel();
      const res = await fetch(`${API_BASE}/api/voice/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          text: cleanText,
          options: {
            ...options,
            voiceId: options.voiceId || voiceId,
            voice: options.voice || getAgentXaiVoice(options.agentId),
            summarize: true,
            summaryLevel
          }
        })
      });

      if (res.ok) {
        const blob = await res.blob();
        try {
          await playAudioBuffer(blob, {
            onStart: () => setIsSpeaking(true),
            onEnd: () => setIsSpeaking(false),
          });
        } catch (playErr) {
          console.error('Audio play failed:', playErr);
          setIsSpeaking(false);
          await fallbackTTS(text, options.agentId);
        }
        return;
      }
    } catch (err) {
      console.error('Summary TTS request failed, using fallback:', err.message);
    }

    fallbackTTS(text, options.agentId);
  }, [voiceSettings, voiceId]);

  const fallbackTTS = async (text, agentName) => {
    const cleanText = cleanTextForSpeech(text);
    const settings = voiceSettings || {};
    const lang = settings.language === 'fr' ? 'fr-FR' : 'en-US';
    await speakNative({
      text: cleanText,
      lang,
      rate: settings.age === 'old' ? 0.85 : undefined,
      agentName,
      onStart: () => setIsSpeaking(true),
      onEnd: () => setIsSpeaking(false),
    });
  };

  const stopSpeaking = useCallback(() => {
    stopAllAudio();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setIsSpeaking(false);
  }, []);

  const testVoice = useCallback(async (language) => {
    if (isTestingVoice) return { success: false, error: 'Test already running' };
    setIsTestingVoice(true);
    try {
      unlockAudio();
      const lang = language || voiceSettings?.language || 'en';
      const text = testPhrases[lang] || testPhrases.en;
      const res = await fetch(`${API_BASE}/api/voice/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ text, options: { voiceId, force: true } })
      });
      if (!res.ok) throw new Error(`TTS request failed: ${res.status}`);
      const blob = await res.blob();
      await playAudioBuffer(blob, {
        onStart: () => setIsSpeaking(true),
        onEnd: () => setIsSpeaking(false),
      });
      return { success: true };
    } catch (err) {
      setIsSpeaking(false);
      return { success: false, error: err.message || String(err) };
    } finally {
      setIsTestingVoice(false);
    }
  }, [voiceSettings, voiceId, isTestingVoice]);

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return {
    isListening,
    isSpeaking,
    isTestingVoice,
    transcript,
    voiceSettings,
    voiceStatus,
    recordingDuration,
    formatDuration,
    voices,
    voicesByLang,
    voiceId,
    setVoiceId,
    startListening,
    stopListening,
    speak,
    speakSummary,
    stopSpeaking,
    testVoice,
    fetchVoiceSettings,
    updateVoiceSettings,
    setTranscript
  };
}
